import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  FormLayout,
  TextField,
  Button,
  Text,
  BlockStack,
  Box,
  InlineStack,
  Checkbox,
} from '@shopify/polaris';
import { useAdminAuthStore } from '../../store/authStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

export function LoginPage() {
  const navigate = useNavigate();
  const login = useAdminAuthStore((state) => state.login);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleEmailChange = useCallback((value: string) => setEmail(value), []);
  const handlePasswordChange = useCallback((value: string) => setPassword(value), []);
  const handleRememberMeChange = useCallback((value: boolean) => setRememberMe(value), []);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/auth/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error?.message || 'Invalid email or password');
        return;
      }

      // Store the tokens and admin data
      login(data.data.admin, data.data.accessToken, data.data.refreshToken);
      navigate('/');
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [email, password, navigate, login]);

  return (
    <Box padding="800" background="bg-surface-secondary" minHeight="100vh">
      <InlineStack align="center">
        <Box maxWidth="400px" width="100%">
          <Card>
            <BlockStack gap="600">
              <BlockStack gap="200">
                <Text as="h1" variant="headingLg" alignment="center">
                  Admin Login
                </Text>
                <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                  Corporate Gifting Platform
                </Text>
              </BlockStack>

              {error && (
                <Box padding="300" background="bg-surface-critical" borderRadius="200">
                  <Text as="p" variant="bodyMd" tone="critical">
                    {error}
                  </Text>
                </Box>
              )}

              <FormLayout>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  autoComplete="email"
                  placeholder="admin@gifting.com"
                />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={handlePasswordChange}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                />
                <Checkbox
                  label="Remember me"
                  checked={rememberMe}
                  onChange={handleRememberMeChange}
                />
                <Button
                  variant="primary"
                  fullWidth
                  onClick={handleSubmit}
                  loading={loading}
                >
                  Sign In
                </Button>
              </FormLayout>

              <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                Sign in with your admin credentials
              </Text>
            </BlockStack>
          </Card>
        </Box>
      </InlineStack>
    </Box>
  );
}
